import React, { ChangeEvent, useCallback, useState, useMemo, memo, useEffect } from 'react';
import { useGetAllVideosLazyQuery, useGetAllCoverVideosLazyQuery, Video, useVideoSubtitlesLazyQuery } from "@api/graphql-codegen";
import VideoLibrarySelection from "@components/video-library-selection/VideoLibrarySelection";
import styles from './VideoForm.module.scss';
import Button from '@components/button/Button';
import IconVideo from '@assets/icons/content_type_video.svg?react';
import IconTrash from '@assets/icons/trash.svg?react';
import ContentVideo from '@components/content-video/ContentVideo';
import Spinner from '@components/spinner/Spinner';
import SubtitlesList from '../subtitles-list/SubtitlesList';
import SubtitlesUploadModal from '../subtitles-upload-modal/SubtitlesUploadModal';
import { logger } from '../../utils/logger';
import { useVideoTranslation } from '@src/hooks/useVideoTranslation';
import { useToast } from '@src/hooks';
import { TRANSLATION_LANGUAGES } from '@types';
import SelectMulti from '../select-multi/SelectMulti';

type Props = {
  data: string | undefined;
  meta?: string | null;
  dataFile?: File | undefined;
  onChange: (e: File | undefined) => void;
  onChangeVideo: (video: Video | null) => void;
  isUploading?: boolean;
  uploadingPercent?: number;
  isDisabled?: boolean;
  noSubTitle?: boolean;
  fileUploadFinished?: boolean;
  isCoverVideo?: boolean;
  isCourse?: boolean;
};

// const MuxDomain = 'https://stream.mux.com';
const URLObj = window.URL || window.webkitURL;
// const TenMB = 1024 * 1024 * 10;
const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];

function bytesToSize(bytes: number) {  
  if (bytes === 0) return '0 Byte';
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return Math.round(bytes / Math.pow(1024, i)) + ' ' + sizes[i];
}

function VideoForm(props: Props) {
  const { data, meta, dataFile, onChange, isUploading, uploadingPercent, isDisabled, noSubTitle, fileUploadFinished, 
    onChangeVideo, isCoverVideo = false, isCourse = false } = props;

  const [showUploadSubtitles, setShowUploadSubtitles] = useState(false);
  const [getAllVideos, { data: videoRes, loading: allVideoLoading }] = useGetAllVideosLazyQuery();
  const [getAllCoverVideos, { data: coverVideoRes, loading: allCoverVideoLoading }] = useGetAllCoverVideosLazyQuery();
  const [getSubtitles, { data: subTitleData, refetch }] = useVideoSubtitlesLazyQuery({ fetchPolicy: 'network-only' })

  const [video, setVideo] = useState<Video | null>();
  const { successToast, errorToast } = useToast();

  const [subtitlesRefreshKey, setSubtitlesRefreshKey] = useState(0);
  const [languages, setLanguages] = useState<string[]>([]);

  const {
    generateEnglishCaptions,
    createTranslationJob,
    downloadEnglishSubtitles,
    downloadTranslationSubtitles,
    englishCaptionJob,
    translationJob,
    loading,
    error,
    isProcessing,
    overallProgress,
    reset,
    stopTranslationJob,
  } = useVideoTranslation({
    pollingInterval: 3000,
    onEnglishCaptionsComplete: (job) => {
      successToast('English captions ready, Please download!');
      setSubtitlesRefreshKey(prev => prev + 1);
      refetch();
    },
    onTranslationComplete: (job) => {
      successToast(`Translations completed, Please download!`);
      setSubtitlesRefreshKey(prev => prev + 1);
      refetch();
    },
    onError: (error) => {
      errorToast(`Translation error, Please try again!`);
    },
  });

  useEffect(() => {
    // FIXME: we might want to add Video Library into Course. then `isCourse` will be removed.
    if (!isCourse) {
      if (isCoverVideo) {
        getAllCoverVideos()
      } else {
        getAllVideos()
      }
    }
  }, [getAllCoverVideos, getAllVideos, isCoverVideo, isCourse])

  const videoData = useMemo(() => {
    let videoData: Video[] = []
    if (videoRes) {
      videoData = videoRes.allVideos as Video[]
    }
    if (coverVideoRes) {
      videoData = coverVideoRes.allCoverVideos as Video[]
    }

    return videoData
  }, [coverVideoRes, videoRes])
  const videoLoading = allVideoLoading || allCoverVideoLoading

  const isUploaded = useMemo(() => !isUploading && (data ?? '').startsWith('https:'), [isUploading, data]);
  const videoLink = useMemo(() => {
    let _videoLink: string | null = ''
    if (isUploaded && data) {
      _videoLink = data
    }
    if (isUploaded && videoData.length > 0 && _videoLink) {
      const v = videoData.find(v => `${v.data}` === `${_videoLink}`)
      setVideo(v)
    } else {
      setVideo(null)
    }
    return _videoLink
  }, [data, videoData, isUploaded])
  
  const [metaData, assetId, playbackId] = useMemo(() => {
    if (meta && isUploaded) {
      try {
        const metaData = JSON.parse(meta);
        const playbackId = (metaData?.playback_ids || []).find((p: { policy: string; id: string; }) => p.policy === 'public')?.id
        return [metaData, metaData?.id, playbackId];
      } catch (e) {
        logger.debug(`[Can't parse meta info]`);
      }
    }
    if (data && isUploaded) {
      return [undefined, undefined, /https:\/\/stream.mux.com\/(.*)\.m3u8/g.exec(data)?.[1] || undefined];
    }
    return [undefined, undefined, undefined];
  }, [meta, isUploaded, data]);

  useEffect(() => {
    if (assetId || playbackId) {
      getSubtitles({
        variables: {
          assetId,
          playbackId,
        }
      });
    }
  }, [getSubtitles, assetId, playbackId]);

const hasEnglishSubtitles = useMemo(() => {
  if (!subTitleData?.videoSubtitles || subTitleData.videoSubtitles.length === 0) {
    return false;
  }
  
  // Check if any subtitle has language 'en' or 'english'
  return subTitleData.videoSubtitles.some(subtitle => 
    subtitle?.languageCode?.toLowerCase() === 'en' || 
    subtitle?.name?.toLowerCase().includes('english')
  );
}, [subTitleData?.videoSubtitles]);

  // Also check if English caption job is completed OR if we have existing English subtitles
  const canGenerateSubtitles = useMemo(() => {
    return englishCaptionJob?.status === 'completed' || hasEnglishSubtitles;
  }, [englishCaptionJob?.status, hasEnglishSubtitles]);

  const allLanguages = useMemo(() => {
    return TRANSLATION_LANGUAGES.map(lang => {
      return {
        label: lang.name,
        value: lang.code.toLowerCase()
      }
    })
  }, [])

  const handleSelLanguages = useCallback((o: string[]) => {
    setLanguages(o);
  }, []);

  const handleStopTranslation = useCallback(() => {
    setLanguages([]);
    stopTranslationJob()
  }, [stopTranslationJob])

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      window.alert('No file selected');
    } else {
      onChange(file);
    }
  };

  const handleClear = useCallback(() => onChange(undefined), [onChange]);

  const handleOpenUploadSubtitlesModal = useCallback(() => {
    setShowUploadSubtitles(true);
  }, [setShowUploadSubtitles]);

  const handleCloseUploadSubtitlesModal = useCallback(() => {
    setShowUploadSubtitles(false);
  }, [setShowUploadSubtitles]);

  const videoFileBlob = useMemo(() => dataFile?.size ? URLObj.createObjectURL(dataFile) : undefined, [dataFile]);

  const handleTranslateVideo = async () => {
    await generateEnglishCaptions(assetId);
  };

  const handleTranslateToLanguages = async (assetId: string, languages: string[] = []) => {
    await createTranslationJob(assetId, languages);
  };

  return (
    <>
      {!isCourse && (
        <VideoLibrarySelection
          className={styles.videoLibrary}
          inDropDown={false}
          value={video}
          onChange={(v) => onChangeVideo(v)}
          renderLabel={() => <div className={styles.videoLibraryTitle}>Video Library</div>}
          options={videoData}
          showSearch={true}
          loading={videoLoading}
        />
      )}

      {!dataFile && !isUploaded && <div className={styles.emptyView}>
        <div className={styles.uploader}>
          <input accept={'video/*'} type="file" className={styles.fileInput} onChange={handleChange}/>
          <Button label={fileUploadFinished ? 'Processing...' : isUploading ? `Uploading: ${uploadingPercent}%` : 'Upload video'} color={'orange'} outline={true} className={`${styles.inputButton} ${isUploading ? styles.white : ''}`}
            disabled={isDisabled}
            isLoading={isUploading}
            leftIcon={isUploading ? <Spinner width={20} height={20} /> : <IconVideo/>}
          />
          <div className={styles.uploaderText}>{'or drag & drop video file here'}</div>        
        </div>
      </div>}
      {dataFile && <><div className={styles.row}>
        <div className={styles.title}>{fileUploadFinished ? 'Processing...' : isUploading ? `Uploading: ${uploadingPercent}%` : `Video ${isUploaded ? '' : '(not uploaded yet)'}`}</div>
        <div className={styles.fileContent}>
          <div className={styles.fileTitle}>{dataFile.name} - {bytesToSize(dataFile.size)}</div>
          <Button className={styles.buttonDelete} disabled={isDisabled} onClick={handleClear} size={'small'} label={'Delete'} leftIcon={<IconTrash className={styles.iconTrash}/>} outline={true}/>
        </div>
        <div className={styles.videoWrapper}>
          <ContentVideo data={videoFileBlob} autoPlay={false} isVideoFile />
        </div>
      </div>
      </>}
      {isUploaded && !dataFile && videoLink ? <>
        <div className={styles.row}>
          <div className={styles.title}>Video</div>
          <div className={styles.videoWrapper}>
            <ContentVideo data={data} autoPlay={false} metaData={metaData} />
          </div>
          <div className={styles.customUploaderContainer}>
            <input accept={'video/*'} type="file" className={styles.fileInput} onChange={handleChange}/>
            <Button label={fileUploadFinished ? 'Processing...' : isUploading ? `Uploading: ${uploadingPercent}%` : 'Replace video'} color={'orange'} outline={true} className={`${styles.inputButton} ${isUploading ? styles.white : ''}`}
              disabled={isDisabled || isUploading}
              isLoading={isUploading}
              leftIcon={isUploading ? <Spinner width={20} height={20} /> : <IconVideo/>}
            />
          </div>
        </div>

        <div className={styles.videoTranslation}>
          <Button disabled={loading || isProcessing} label={englishCaptionJob?.status === 'processing' ? `Processing... ${overallProgress}%` : 'Translate English Subtitle'} color='orange' onClick={handleTranslateVideo} className={styles.videoTranslateBtn}/>
          {englishCaptionJob ? (
            <div className={styles.translationStatus}>
              <h3>English Captions</h3>
              <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                <strong>Progress: {englishCaptionJob.progressPercentage}%</strong>
                {englishCaptionJob.subtitleUrl && (
                  <Button label='Download English Subtitles' onClick={downloadEnglishSubtitles} size='small' color='green' className={styles.downloadBtn} />
                )}
              </div>
            </div>
          ) : (
            hasEnglishSubtitles && (
              <div className={styles.translationStatus}>
                <h3>English Captions</h3>
                <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                  <span>English subtitles available</span>
                </div>
              </div>
            )
          )}
          {
            <div>
              <SelectMulti
                disabled={loading || isProcessing || !canGenerateSubtitles}
                value={languages ? languages : []}
                onChange={handleSelLanguages}
                label={'Select the Languages which want to translation.'}
                options={allLanguages}
                className={styles.selection}
              />
            </div>
          }
          {
          <div style={{display: 'flex', gap: '12px'}}>
            <Button disabled={loading || isProcessing || !canGenerateSubtitles} label={translationJob?.status === "processing" ? `Processing... ${overallProgress}%` : languages.length === 0 ? 'Generate All Subtitles' : 'Generate Subtitles'} onClick={() => handleTranslateToLanguages(assetId, languages)} className={styles.videoTranslateBtn}/>
            {translationJob?.status === "processing" && <Button label='Stop Generation' onClick={handleStopTranslation} color="red" className={styles.stopTranslateBtn}/>}
          </div>
          }
          {error && (
            <div style={{ color: 'red' }}>
              Error: {error}
              <Button onClick={reset} label='Retry' color='green' size='small' className={styles.retryTranslateBtn} />
            </div>
          )}

          {translationJob && translationJob.languageJobs && (
            <div>
              <div className={styles.translationStatus}>
                <h3>Translations</h3>
                <div><strong>Status:</strong>&nbsp;{translationJob.status}, &nbsp;<strong>Progress: </strong>&nbsp;{translationJob.progressPercentage}%</div>
              </div>
              <div className={styles.translationSubBlock}>
                {translationJob.languageJobs.map((languageJob) => (
                  <div key={languageJob.id} style={{ margin: '10px 0' }} className={styles.translationSubTitle}>
                    <div><strong>{TRANSLATION_LANGUAGES.find(lang => lang.code.toLowerCase() === languageJob.languageCode.toLowerCase())?.name }:&nbsp;</strong> {languageJob.status}</div>
                    {languageJob.subtitleUrl && (
                      <Button onClick={() => downloadTranslationSubtitles(languageJob.languageCode)} label='Download Subtitles' size='small' color='green' className={styles.downloadBtn}/>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        {noSubTitle ? null : (
          <div className={styles.row}>
            <div className={styles.titleWrapper}>
              <div className={styles.title}>Subtitles</div>
              <Button className={styles.uploadBtn} disabled={isDisabled} size={'extraSmall'} onClick={handleOpenUploadSubtitlesModal} outline={true} label={'Upload subtitles'} color={'orange'}/>
            </div>
            {playbackId ? <SubtitlesList key={subtitlesRefreshKey} playbackId={playbackId} id={assetId} subtitles={subTitleData?.videoSubtitles ?? []}/> : null}
          </div>
        )}
      </> : null
      }
      {!noSubTitle && showUploadSubtitles && playbackId && isUploaded ? <SubtitlesUploadModal playbackId={playbackId} id={assetId} onClose={handleCloseUploadSubtitlesModal}/> : null}
    </>
  );
}

export default memo(VideoForm);
